﻿import { getWcBrand } from '../../../src/worldcup/helpers/wcBrand.helper';

/** Left inset for floating group-code row so it does not sit under the back control. */
export const GROUP_CODE_FLOATING_CLUSTER_PADDING_LEFT = 108;

/**
 * Below this width the mascot is nudged toward (and slightly past) the right edge
 * so the squad name keeps a usable column. Covers iPhone SE / compact Android.
 */
export const GROUP_HERO_BANNER_SMALL_SCREEN_WIDTH = 380;

/** Consistent touch expansion for squad banner back controls (min ~44pt feel). */
export const GROUP_HERO_BANNER_BACK_HIT_SLOP = {
  top: 12,
  bottom: 12,
  left: 12,
  right: 12,
} as const;

export const GROUP_HERO_BANNER_COPY = {
  BACK: 'Squads',
  PRESENTED_BY: 'Presented by',
  COMPANY_GROUP: 'Company Group',
  MEMBERS: 'members',
  EMPLOYEES: 'members',
  GROUP_CODE_LABEL: 'Group code',
  COPY_GROUP_CODE_A11Y: 'Copy group code',
  SHARE_INVITE_A11Y: 'Share squad invite',
  SQUAD_INFO_A11Y: 'Squad info',
  EDIT_SQUAD_NAME_A11Y: 'Edit squad name',
  RENAME_SQUAD_LABEL: 'Rename squad',
  SAVE_SQUAD_NAME: 'Save',
  CANCEL_RENAME: 'Cancel',
} as const;

/**
 * Native share-sheet message template for inviting friends to a squad.
 * Keep in this constants file so copy edits don't require touching the banner component.
 */
export const getShareDialogTitle = (): string => `${getWcBrand().NAME} squad invite`;

export const buildShareMessage = (groupName: string, inviteLink: string): string =>
  `Join my ${getWcBrand().NAME} squad "${groupName}" on Zporti! ${inviteLink}`;

/** White-with-alpha used for back/share chrome icons over the brand banner gradient. */
export const GROUP_HERO_BANNER_ICON_COLOR = 'rgba(255,255,255,0.9)' as const;
/** Slightly darker variant used on group-code copy icon next to the strip. */
export const GROUP_HERO_BANNER_ICON_COLOR_MUTED = 'rgba(255,255,255,0.85)' as const;
/** Slightly brighter variant for the floating group-code icon (richer contrast on dark banner). */
export const GROUP_HERO_BANNER_ICON_COLOR_STRONG = 'rgba(255,255,255,0.88)' as const;

/** Dark glass surfaces for banner action chips — readable on light brand images. */
export const GROUP_HERO_BANNER_ACTION_SURFACE = 'rgba(0,0,0,0.52)' as const;
export const GROUP_HERO_BANNER_ACTION_SURFACE_BORDER = 'rgba(255,255,255,0.32)' as const;
export const GROUP_HERO_BANNER_ACTION_ICON_COLOR = 'rgba(255,255,255,0.95)' as const;
export const GROUP_HERO_BANNER_ACTION_LABEL_COLOR = 'rgba(255,255,255,0.92)' as const;

/** Subtle radial orb tint (top-right banner glow). */
export const GROUP_HERO_BANNER_ORB_GRADIENT: readonly [string, string] = [
  'rgba(255,255,255,0.14)',
  'transparent',
];

/** Diagonal shimmer overlay sweeping across the banner. */
export const GROUP_HERO_BANNER_SHIMMER_GRADIENT: readonly [string, string, string] = [
  'transparent',
  'rgba(255,255,255,0.04)',
  'transparent',
];

/** TV display: banner is a prominent header strip, but leaves room for podium + table rows. */
export const GROUP_HERO_BANNER_TV_HEIGHT_RATIO = 0.3;
export const GROUP_HERO_BANNER_TV_MIN_HEIGHT = 240;
export const GROUP_HERO_BANNER_TV_MAX_HEIGHT = 420;

export const GROUP_HERO_BANNER_TV_BADGE_SIZE = 168;
export const GROUP_HERO_BANNER_TV_CREST_SIZE = 148;
export const GROUP_HERO_BANNER_TV_LOGO_IMAGE_SIZE = 160;
/** Company squads without an uploaded logo use the squad crest at logo scale. */
export const GROUP_HERO_BANNER_COMPANY_CREST_SIZE = 76;
export const GROUP_HERO_BANNER_TV_MASCOT_WIDTH = 260;
export const GROUP_HERO_BANNER_TV_MASCOT_HEIGHT = 286;
/** Right inset on the TV hero row so title text clears the mascot. */
export const GROUP_HERO_BANNER_TV_MASCOT_RESERVE = 248;

/** Pin crest/logo + squad name to the banner bottom (matches normal-mode hero inset). */
export const GROUP_HERO_BANNER_HERO_BOTTOM_INSET = 22;
export const GROUP_HERO_BANNER_TV_HERO_BOTTOM_PADDING = 22;

export const getGroupHeroBannerTvHeight = (windowHeight: number): number =>
  Math.min(
    GROUP_HERO_BANNER_TV_MAX_HEIGHT,
    Math.max(
      GROUP_HERO_BANNER_TV_MIN_HEIGHT,
      Math.round(windowHeight * GROUP_HERO_BANNER_TV_HEIGHT_RATIO),
    ),
  );

/**
 * When a company uploads a marketing banner, anchor the crop on the left so the
 * image's center tagline sits behind the mascot (right) instead of under squad text.
 */
export const GROUP_HERO_BANNER_IMAGE_CONTENT_POSITION = 'left center' as const;

/** Left-side darkening so overlaid squad name/meta stay readable on busy brand art. */
export const GROUP_HERO_BANNER_LEFT_SCRIM_GRADIENT: readonly [string, string] = [
  'rgba(0,0,0,0.72)',
  'transparent',
];

/** Hero text/logo must paint above brand scrims. */
export const GROUP_HERO_BANNER_HERO_FOREGROUND_Z_INDEX = 6;

/** Fraction of banner width covered by the left scrim. */
export const GROUP_HERO_BANNER_LEFT_SCRIM_WIDTH_RATIO = 0.56;

/** Max width for hero text column when a full-bleed banner image is present (TV only). */
export const GROUP_HERO_BANNER_HERO_MAX_WIDTH_RATIO = 0.42;

/**
 * Right-side space the phone hero reserves for the mascot, so the name/meta column
 * ends just before the monkey. Must stay in sync with the mascot / mascotSmall
 * geometry in GroupHeroBanner.styles.ts: (right offset + image width + ~8px gap).
 */
export const GROUP_HERO_BANNER_MASCOT_RESERVE = 174; // 16 right + 150 width + 8 gap
export const GROUP_HERO_BANNER_MASCOT_RESERVE_SMALL = 132; // 2 right + 122 width + 8 gap
/** Hero text column left inset for company squads (matches heroInfoWithLogoUrl). */
export const GROUP_HERO_BANNER_HERO_LEFT_INSET = 112;
/** Never shrink the name column below this, even on very narrow devices. */
export const GROUP_HERO_BANNER_MIN_NAME_COLUMN = 96;

/** TV mode: cap width so large title does not run into the image tagline. */
export const GROUP_HERO_BANNER_TV_HERO_MAX_WIDTH_RATIO = 0.44;

export const getGroupHeroBannerHeroMaxWidth = (
  windowWidth: number,
  tvDisplayMode: boolean,
): number =>
  Math.round(
    windowWidth *
      (tvDisplayMode
        ? GROUP_HERO_BANNER_TV_HERO_MAX_WIDTH_RATIO
        : GROUP_HERO_BANNER_HERO_MAX_WIDTH_RATIO),
  );

export const getGroupHeroBannerLeftScrimWidth = (windowWidth: number): number =>
  Math.round(windowWidth * GROUP_HERO_BANNER_LEFT_SCRIM_WIDTH_RATIO);

/**
 * Phone hero: width of the squad name/meta column when a banner image is present.
 * Geometry-based (left inset → mascot edge) instead of a fixed width ratio, so the
 * name uses all the space up to the mascot rather than being capped at ~42% of the
 * screen. Paired with dropping the name's mascot paddingRight (see groupNameOnBannerImage)
 * so the two reservations no longer stack and crush the text on small screens.
 */
export const getGroupHeroBannerNameColumnMaxWidth = (
  windowWidth: number,
  isSmallScreen: boolean,
): number =>
  Math.max(
    windowWidth
      - GROUP_HERO_BANNER_HERO_LEFT_INSET
      - (isSmallScreen
        ? GROUP_HERO_BANNER_MASCOT_RESERVE_SMALL
        : GROUP_HERO_BANNER_MASCOT_RESERVE),
    GROUP_HERO_BANNER_MIN_NAME_COLUMN,
  );
